import React, { useEffect } from "react";
import FollowUpListing from "./FollowUpListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/FollowUpSlice";
import { useGetFollowUpPaginationQuery } from "src/services/FollowUpServices";
import { useNavigate, useParams } from "react-router-dom";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { RxPencil1 } from "react-icons/rx";
import { formatedDateTimeIntoIst } from "src/utils/formUtils/dateTimeFormate";
import { Chip, Tooltip } from "@mui/material";
import { UserModuleActionTypes, UserModuleNameTypes } from "src/models/userAccess/UserAccess.model";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";

type Props = {};

const FollowUpListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const { inquiryId = "" } = useParams();
  const navigate = useNavigate();

  //STATE
  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.followUp);

  const { data, isLoading, isFetching } = useGetFollowUpPaginationQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: [
      "is_communicated",
      "communication_summary",
      "communication_method",
      "interest_level",
      "next_followUp_required",
      "contact_them",
      "next_followUp_date",
      "new_batch_start",
      "followup_batch_start_course",
      "next_followup_reason",
      "interested_course",
      "disposition_one",
      "disposition_two",
    ],
    page: page,
    filterBy: [
      {
        fieldName: "inquiry_id",
        value: [inquiryId],
      },
    ],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });
  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setIsTableLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const getActionOptions = (row: any) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () =>
          navigate(`/inquiries/edit/${inquiryId}/followup/edit/${row?._id}`),
        actionName: UserModuleActionTypes.INQUIRIES_EDIT_FOLLOWUP_EDIT,
        moduleName: UserModuleNameTypes.INQUIRIES
      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: "createdAt",
      headerName: "Date - Time",
      extraClasses: "py-1 min-w-[120px]",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div>
          <div className="text-slate-700 font-medium">
            {formatedDateTimeIntoIst(row.createdAt, "DD MMM yyyy")}
          </div>
          <div className="text-[13px] font-medium text-slate-400">
            {formatedDateTimeIntoIst(row.createdAt, "hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      field: "communicatedByName",
      extraClasses: "min-w-[130px] py-2 max-w-[170px]",
      headerName: "Taken By",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => (
        <span className="capitalize text-[13px]"> {row?.communicatedByName} </span>
      ),
    },
    {
      field: "contact_them",
      extraClasses: "py-2 min-w-[220px] ",
      headerName: `Next FollowUp On`,
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => (
        <span>
          {" "}
          {row?.contact_them === "ON_EVENT" ? (
            row?.followup_batch_start_course?.map((course: any) => {
              return (
                <Chip
                  key={course?._id}
                  label={course?.courseName}
                  className="me-2 my-1"
                />
              );
            })
          ) : (
          row?.contact_them === "ON_DATE" &&  <div>
          <div className="text-slate-700 font-medium">
            {formatedDateTimeIntoIst( 
              row?.next_followUp_date,
              "DD MMM yyyy" 
            )}
          </div>
          <div className="text-[13px] font-medium text-slate-400">
            { row?.next_followup_time !== 'Invalid date' && row?.next_followup_time}
          </div>
        </div>
          )}{" "}
        </span>
      ),
    },
    {
      field: "communication_summary",
      extraClasses: "py-2  overflow-scroll max-w-[350px] max-h-[100px] min-w-[350px]",
      headerName: "Summary",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => {
        return (
            <p className="text-[12px] text-balance"> {row?.communication_summary} </p>
        );
      },
    },
    {
      field: "inquiryLabelName",
      headerName: "Label",
      flex: "flex-[1.5_1.5_0%]",
      extraClasses: "max-w-[80px]",
      align: "center",
      renderCell: (row: any) => (
        <Tooltip title={row?.inquiryLabelName}>
          <span className="text-[12px]"> {row?.inquiryLabelName} </span>
        </Tooltip>
      ),
    },
    {
      field: "next_followUp_required",
      extraClasses: "py-2  min-w-[180px]",
      headerName: "Next FollowUp Required",
      flex: "flex-[1.5_1.5_0%]",
      align:'center',
      renderCell: (row: any) => <span> {row?.next_followUp_required} </span>,
    },

    {
      field: "is_communicated",
      extraClasses: "py-2  min-w-[180px]",
      headerName: "Is Communicated",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => <span> {row?.is_communicated} </span>,
    },
    {
      field: "action",
      headerName: "Action",
      flex: "flex-[1.5_1.5_0%] ",
      renderCell: (row: any) => {
        const options = getActionOptions(row);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];
  const CustomArray = [UserModuleActionTypes.INQUIRIES_EDIT_FOLLOWUP_EDIT]
  return (
    <div>
      <FollowUpListing
        columns={isCheckColumnPermission({
          moduleName: UserModuleNameTypes.INQUIRIES,
          actionName: UserModuleActionTypes.INQUIRIES_EDIT_FOLLOWUP_LIST,
          columns: columns,
          actionArray: CustomArray
        })}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
      />
    </div>
  );
};

export default FollowUpListingWrapper;
